import './Login.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticateUser } from '../../services/authService';
import spottImg from '../../assets/Layer_1.svg';
import { validateRegexEmail, validatePassword } from '../../utils/format';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import { useAuth } from '../../context/AuthContext';
import { useBrandContext } from '../../context/BrandContext';
import GoogleLoginButton from '../../components/GoogleLoginButton/GoogleLoginButton';
import AppleLoginButton from '../../components/AppleLoginButton/AppleLoginButton';
import { isWebView, isAppleDevice } from '../../utils/detectWebContainer';
import { useHeaderContext } from '../../context/HeaderContext';
import { getBuildNumber } from '../../utils/getBuildNumber';

const Login = () => {
  type Inputs = { email: string; password: string };
  const [form, setForm] = useState<Inputs>({ email: '', password: '' });
  type Touched = Partial<Record<keyof Inputs, boolean>>;
  const [touched, setTouched] = useState<Touched>({});
  type Errors = Partial<Record<keyof Inputs, string>>;
  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [errorFetch, setErrorFetch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();

  const webView = isWebView();

  const authContext = useAuth();
  const buildNumber = getBuildNumber(
    webView ? (authContext.appVersion as string) : null
  );
  const socialLoginVersion = 163;

  const authAppleShow = brandContext?.brandData?.features?.authApple;
  const authGoogleShow = brandContext?.brandData?.features?.authGoogle;
  const appleDevice = isAppleDevice() && authAppleShow;

  const keyToNavigateRoute: any = localStorage.getItem('attemptRoute');

  useEffect(() => {
    const email = JSON.parse(sessionStorage.getItem('userEmail')!);
    if (email) {
      setForm({ ...form, email: email });
    }
  }, []);

  const validate = (newInputs: Inputs): Errors => {
    let newErrors: Errors = {};
    if (!newInputs.email.trim()) {
      newErrors.email = 'Campo Obrigatório';
    } else if (!validateRegexEmail(newInputs.email)) {
      newErrors.email = 'Por favor insira um e-mail válido';
    }

    if (!form.password.trim()) {
      newErrors.password = 'Campo Obrigatório';
    } else if (!validatePassword(newInputs.password)) {
      newErrors.password = 'Por favor, atenda aos critérios de senha';
    }

    setErrorsValidate(newErrors);
    return newErrors;
  };

  const handleKeyPress = (event: any) => {
    if (event.key === ' ') {
      event.preventDefault(); // Impede a inserção do espaço
    }
  };

  const handleShowPassword = () => {
    setShowPassword((showPassword) => !showPassword);
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      try {
        const userData = await authenticateUser(
          form.email,
          form.password,
          authContext
        );

        if (keyToNavigateRoute) {
          navigate(`/${brandContext.brandName}${keyToNavigateRoute}`);
          localStorage.removeItem('attemptRoute');
        } else {
          navigate(`/${brandContext.brandName}/`);
        }
        setErrorFetch('');
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        if (error?.response?.status === 404) {
          setErrorFetch('E-mail ou senha inválidos.');
        } else if (error?.message === 'Network Error') {
          setErrorFetch(
            'Sua conexão está instável. Por favor, tente mais tarde.'
          );
        } else {
          setErrorFetch('Falha no servidor. Por favor tente novamente.');
        }
      }
    }
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: 'Faça seu login',
      showLogo: true,
      showActionButtons: false,
    });
    return () => setHeaderConfig({});
  }, [navigate, setHeaderConfig]);

  return (
    <div className='login-container'>
      <form onSubmit={handleSubmit} className='login-form'>
        <div className='login-fields'>
          <Input
            labelName='E-mail'
            htmlFor='email'
            type='text'
            id='email'
            className={
              errorsValidate.email
                ? 'input-pattern error-input'
                : 'input-pattern'
            }
            placeholder='Insira seu e-mail'
            value={form.email}
            onKeyDown={(event) => handleKeyPress(event)}
            onChange={(event) => {
              setForm({ ...form, email: event.target.value.toLowerCase() });
            }}
            onBlur={() => setTouched({ ...touched, email: true })}
            isErrorValidate={errorsValidate.email ? true : false}
            messageError={errorsValidate.email}
            style={{
              textTransform: form.email.length > 0 ? 'lowercase' : 'unset',
            }}
          />

          <Input
            labelName='Senha'
            htmlFor='password'
            type={showPassword ? 'text' : 'password'}
            id='password'
            className={
              errorsValidate.password
                ? 'input-pattern error-input'
                : 'input-pattern'
            }
            placeholder='Insira sua senha'
            value={form.password}
            onKeyDown={(event) => handleKeyPress(event)}
            onChange={(event) => {
              setForm({ ...form, password: event.target.value });
            }}
            onBlur={() => setTouched({ ...touched, password: true })}
            isErrorValidate={errorsValidate.password ? true : false}
            isPassword={true}
            handleShowPassword={handleShowPassword}
            showPassword={showPassword}
            messageError={errorsValidate.password}
            style={{ flexGrow: 1, marginRight: '-19px' }}
          />

          <b className='error-fetch-login'> {errorFetch ? errorFetch : null}</b>
          <a
            className='forgot-password-login'
            href={`/${brandContext.brandName}/forgotPassword`}
          >
            Esqueceu a senha?
          </a>
          <Button
            type='submit'
            children='Entrar'
            loading={isLoading}
            disabled={isLoading}
          />

          {!webView || buildNumber >= socialLoginVersion ? (
            <>
              <div className='divider-login'>OU</div>

              {authGoogleShow && <GoogleLoginButton />}
              {appleDevice && <AppleLoginButton />}
            </>
          ) : null}

          <a
            className='keep-without-login'
            href={`/${brandContext.brandName}/`}
            style={{ marginTop: 20 }}
          >
            Continuar sem login
          </a>
        </div>
        <div className='signup-link-container'>
          Ainda não tem uma conta?&nbsp;
          <a href={`/${brandContext.brandName}/register`}>Crie aqui</a>
        </div>
        {brandContext.brandName.toLowerCase() === 'spott' && (
          <div className='footer-spott-img'>
            <img alt='footer-spott-img' src={spottImg} />
          </div>
        )}
      </form>
    </div>
  );
};

export default Login;
